import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Play, Square, Vote, X, ChevronDown, ChevronUp, RefreshCw, Users, CheckCircle } from 'lucide-react';
import { votingAdminAPI } from '../lib/api';
import { PageHeader, LoadingPage, StatCard, EmptyState, Modal, FormGroup } from '../components/shared';
import toast from 'react-hot-toast';

const REFRESH_INTERVAL_MS = 5000;

export default function PollAdminPage() {
  const [loading, setLoading] = useState(true);
  const [polls, setPolls] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const intervalRef = useRef(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [options, setOptions] = useState([{ label: '', description: '' }, { label: '', description: '' }]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await votingAdminAPI.getAll();
      setPolls(r.data || []);
      setLastRefreshed(new Date());
    } catch {
      if (!silent) toast.error('Failed to load polls.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    intervalRef.current = setInterval(() => load(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [load]);

  const resetForm = () => {
    setTitle(''); setDescription('');
    setOptions([{ label: '', description: '' }, { label: '', description: '' }]);
  };

  const validOptions = options.filter(o => o.label.trim());
  const canCreate = title.trim() && validOptions.length >= 2;

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      await votingAdminAPI.create({
        title: title.trim(),
        description: description.trim() || null,
        options: validOptions,
      });
      toast.success('Poll created as draft.');
      setShowCreate(false); resetForm(); load(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create poll.');
    } finally { setCreating(false); }
  };

  const handleStatus = async (poll, status) => {
    const verb = status === 'active' ? 'Open' : 'Close';
    if (!window.confirm(`${verb} voting for "${poll.title}"?`)) return;
    try {
      await votingAdminAPI.updateStatus(poll.id, { status });
      toast.success(status === 'active' ? 'Voting is now open.' : 'Voting closed.');
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status.');
    }
  };

  const handleDelete = async (poll) => {
    if (!window.confirm(`Delete draft poll "${poll.title}"?`)) return;
    try {
      await votingAdminAPI.remove(poll.id);
      toast.success('Poll deleted.');
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete poll.');
    }
  };

  const addOption = () => setOptions([...options, { label: '', description: '' }]);
  const removeOption = (i) => options.length > 2 && setOptions(options.filter((_, x) => x !== i));
  const setOption = (i, field, val) => {
    const next = [...options]; next[i] = { ...next[i], [field]: val }; setOptions(next);
  };

  if (loading) return <LoadingPage />;

  const activePoll = polls.find(p => p.status === 'active');
  const totalVotes = polls.reduce((sum, p) => sum + Number(p.total_votes || 0), 0);

  return (
    <div>
      <PageHeader
        eyebrow="Grand Finale"
        title="Live Voting"
        subtitle="Audience tiebreaker polls · voters scan the QR at futurumawards.com/vote"
        actions={
          <>
            <button className="btn btn-ghost btn-sm" onClick={() => load()}>
              <RefreshCw size={14} /> Refresh
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => { resetForm(); setShowCreate(true); }}>
              <Plus size={14} /> Create Poll
            </button>
          </>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Polls" value={polls.length} icon={Vote} color="teal" />
        <StatCard label="Currently Open" value={activePoll ? 1 : 0} icon={CheckCircle} color="green"
          sub={activePoll ? activePoll.title : 'No poll open'} />
        <StatCard label="Votes Cast" value={totalVotes} icon={Users} color="gold" />
      </div>

      {polls.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={Vote}
            title="No polls yet"
            description="Create a poll ahead of the finale so it is ready to open the moment a tiebreaker is needed."
            action={<button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}><Plus size={14} /> Create Poll</button>}
          />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {polls.map(poll => {
            const isOpen = expanded === poll.id;
            const votes = Number(poll.total_votes || 0);
            const opts = poll.options || [];
            const leader = Math.max(...opts.map(o => Number(o.vote_count || 0)), 0);

            return (
              <div key={poll.id} className={`card ${poll.status === 'active' ? 'card-teal' : ''}`}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, cursor: 'pointer' }}
                  onClick={() => setExpanded(isOpen ? null : poll.id)}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', margin: 0 }}>{poll.title}</h3>
                      <span className={`badge badge-${poll.status}`}>
                        {poll.status === 'active' ? 'Open' : poll.status}
                      </span>
                    </div>
                    {poll.description && (
                      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0 0 6px' }}>{poll.description}</p>
                    )}
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: 0, fontFamily: 'var(--font-mono)' }}>
                      {votes} vote{votes !== 1 ? 's' : ''} · {opts.length} candidate{opts.length !== 1 ? 's' : ''}
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    {poll.status === 'draft' && (
                      <>
                        <button className="btn btn-teal btn-sm" onClick={e => { e.stopPropagation(); handleStatus(poll, 'active'); }}>
                          <Play size={13} /> Open
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); handleDelete(poll); }}>
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                    {poll.status === 'active' && (
                      <button className="btn btn-danger btn-sm" onClick={e => { e.stopPropagation(); handleStatus(poll, 'closed'); }}>
                        <Square size={13} /> Close
                      </button>
                    )}
                    {isOpen ? <ChevronUp size={16} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={16} style={{ color: 'var(--text-muted)' }} />}
                  </div>
                </div>

                {isOpen && (
                  <div style={{ marginTop: 20, paddingTop: 18, borderTop: 'var(--border-subtle)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {opts.map(o => {
                        const c = Number(o.vote_count || 0);
                        const pct = votes > 0 ? Math.round((c / votes) * 100) : 0;
                        const isLeader = poll.status !== 'draft' && c > 0 && c === leader;
                        return (
                          <div key={o.id}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6, gap: 12 }}>
                              <div style={{ minWidth: 0 }}>
                                <span style={{ fontWeight: 500, color: isLeader ? 'var(--gold)' : 'var(--text-primary)' }}>{o.label}</span>
                                {o.description && (
                                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}> — {o.description}</span>
                                )}
                              </div>
                              {poll.status !== 'draft' && (
                                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                                  {c} · {pct}%
                                </span>
                              )}
                            </div>
                            {poll.status !== 'draft' && (
                              <div style={{ height: 8, borderRadius: 'var(--radius-full)', background: 'rgba(128,128,128,0.12)', overflow: 'hidden' }}>
                                <div style={{
                                  height: '100%', width: `${pct}%`, minWidth: c > 0 ? 6 : 0,
                                  borderRadius: 'var(--radius-full)',
                                  background: isLeader ? 'linear-gradient(90deg, var(--gold), var(--gold-light))' : 'var(--teal-dim)',
                                  transition: 'width var(--duration-slow) var(--ease-out)',
                                }} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {poll.status === 'active' && (
                      <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 16, marginBottom: 0, textAlign: 'center' }}>
                        Live · auto-refreshes every 5s
                        {lastRefreshed && ` · updated ${lastRefreshed.toLocaleTimeString()}`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Poll">
        <FormGroup label="Poll title" required>
          <input className="form-input" value={title} onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Tiebreaker — Third Place" style={{ width: '100%' }} />
        </FormGroup>

        <FormGroup label="Description">
          <input className="form-input" value={description} onChange={e => setDescription(e.target.value)}
            placeholder="Optional context shown to voters" style={{ width: '100%' }} />
        </FormGroup>

        <div className="eyebrow" style={{ marginBottom: 10, marginTop: 6 }}>Candidates (minimum 2)</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 14 }}>
          {options.map((o, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input className="form-input" value={o.label} onChange={e => setOption(i, 'label', e.target.value)}
                  placeholder={`Candidate ${i + 1} name`} style={{ width: '100%' }} />
                <input className="form-input" value={o.description} onChange={e => setOption(i, 'description', e.target.value)}
                  placeholder="Project title (optional)" style={{ width: '100%', fontSize: '0.85rem' }} />
              </div>
              {options.length > 2 && (
                <button className="btn btn-ghost btn-sm" onClick={() => removeOption(i)} style={{ marginTop: 4 }}>
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
        </div>

        <button className="btn btn-secondary btn-sm" onClick={addOption} style={{ marginBottom: 20 }}>
          <Plus size={13} /> Add candidate
        </button>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={!canCreate || creating}>
            {creating ? 'Creating…' : 'Create as Draft'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
